import { bindable, computedFrom, autoinject, transient } from "aurelia-framework";
import { StatusReasonClientService } from "../../../services/status-reason/status-reason-client.service";
import { StatusModel, type StatusReasonModel, StatusReasonTransitionModel } from "../../../models/status-reason/status-reason.models";
import * as Enumerable from "linq";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@transient()
export class StatusReasonControl {

    private _countPromise: number = 0;

    statuses: StatusModel[] = [];
    statusReasonsByStatusId: { [statusId: number]: StatusReasonModel[] } = {};

    @bindable
    statusId: number;

    @bindable
    statusLabel: string = "Status";

    @bindable
    statusPlaceholder: string = "Status";

    @bindable
    statusReasonLabel: string = "Status Reason";

    @bindable
    statusReasonPlaceholder: string = "Status Reason";

    @bindable
    statusReasonLogNotesLabel: string = "Notes";

    @bindable
    statusReasonLogNotesPlaceholder: string = "Notes";

    @bindable
    nextStatusReasonLogNotes: string;

    @bindable
    useNotes: boolean = false;

    @bindable
    committedStatusReasonId: number;

    @bindable
    currentStatusReasonId: number;

    @bindable
    entityTypeId: number;

    @bindable
    readonly: boolean = false;

    @bindable
    disabled: boolean = false;

    @bindable
    setDefaultStatusReason: boolean = false;

    @bindable
    setDefaultStatus: boolean = false;

    @bindable
    showLabels: boolean = true;

    @computedFrom(
        Reflection.getPropertyPath<StatusReasonControl>(z => z.statusReasonsByStatusId),
        Reflection.getPropertyPath<StatusReasonControl>(z => z.committedStatusReasonId)
    )
    public get committedStatusReason(): StatusReasonModel {
        return this.statusReasonsByStatusId && this.statusId ?
            this.statusReasonsByStatusId[this.statusId].filter(x => x.statusReasonId == this.committedStatusReasonId)[0] : null;
    }

    @computedFrom(
        Reflection.getPropertyPath<StatusReasonControl>(z => z.statusId),
        Reflection.getPropertyPath<StatusReasonControl>(z => z.currentStatusReasonId)
    )
    public get statusReasonParameters(): string {
        return (this.statusId ? this.statusId : null) + "," + (this.committedStatusReasonId ? this.committedStatusReasonId : null);
    }

    @computedFrom(
        Reflection.getPropertyPath<StatusReasonControl>(z => z.entityTypeId),
        Reflection.getPropertyPath<StatusReasonControl>(z => z.currentStatusReasonId)
    )
    public get statusParameters(): string {
        return (this.entityTypeId ? this.entityTypeId : null) + ",0," + (this.committedStatusReasonId ? this.committedStatusReasonId : null);
    }

    constructor(private statusReasonClientService: StatusReasonClientService, private element: Element) { }

    bind() {
        if (this.entityTypeId) {
            this.loadAvailableTransitions();
        }
    }

    currentStatusReasonIdChanged(newValue: number, oldValue: number) {
        const newStatusReason = newValue ? this.statusReasonsByStatusId[this.statusId].filter(x => x.statusReasonId == newValue)[0] : null;
        const oldStatusReason = oldValue ? this.statusReasonsByStatusId[this.statusId].filter(x => x.statusReasonId == oldValue)[0] : null;
        const customEvent: CustomEvent = new CustomEvent(
            "current-status-reason-changed",
            {
                detail: { newValue: newStatusReason, oldValue: oldStatusReason },
                bubbles: true
            }
        );
        this.element.dispatchEvent(customEvent);
    }

    committedStatusReasonIdChanged() {
        if (this.entityTypeId) {
            this.loadAvailableTransitions();
        }
    }

    entityTypeIdChanged() {
        this.loadAvailableTransitions();
    }

    private loadAvailableTransitions() {
        this._countPromise += 1;
        const localCount = this._countPromise;
        this.statusReasonClientService.getAvailableStatusReasonTransitions(this.committedStatusReasonId, this.entityTypeId).then(response => {
            if (localCount != this._countPromise) {
                return;
            }
            const transitions = response.statusReasonTransitions;
            this.statuses = Enumerable.from(transitions).selectMany(x => [x.nextStatusReason.status]).where(x => x != null)
                .distinct(x => x.statusId).toArray();
            this.statusReasonsByStatusId = <{ [statusId: number]: StatusReasonModel[] }>Enumerable.from(transitions).groupBy(x => x.nextStatusReason.statusId).toObject(x => x.key(), x => x.select(z => z.nextStatusReason).distinct(x => x.statusReasonId).toArray());
            if (response.committedStatusReason && !this.statuses.some(x => x.statusId == response.committedStatusReason.statusId)) {
                this.statuses.unshift(response.committedStatusReason.status);
                this.statusReasonsByStatusId[response.committedStatusReason.statusId] = [];
            }
            if (response.committedStatusReason) {
                if (!this.statusReasonsByStatusId[response.committedStatusReason.statusId].some(x => x.statusReasonId == response.committedStatusReason.statusReasonId)) {
                    this.statusReasonsByStatusId[response.committedStatusReason.statusId].unshift(response.committedStatusReason);
                }
                this.statusId = response.committedStatusReason.statusId;
            }

            this.dispatchAvailableTransitionsLoadedEvent(transitions);
        });
    }

    onNextStatusReason(statusReasonId: number) {
        if (statusReasonId && statusReasonId != this.committedStatusReasonId) {
            this.currentStatusReasonId = statusReasonId;
        } else if (this.committedStatusReasonId && statusReasonId == this.committedStatusReasonId) {
            this.currentStatusReasonId = null;
        }
    }

    dispatchAvailableTransitionsLoadedEvent(transitions: StatusReasonTransitionModel[]) {
        const customEvent: CustomEvent = new CustomEvent(
            "available-transitions-loaded",
            {
                detail: { transitions: transitions },
                bubbles: true
            }
        );
        this.element.dispatchEvent(customEvent);
    }
}
